import dayjs from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import localeData from 'dayjs/plugin/localeData';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import preParsePostFormat from 'dayjs/plugin/preParsePostFormat';
import utc from 'dayjs/plugin/utc';
import weekOfYear from 'dayjs/plugin/weekOfYear';

import 'dayjs/locale/ar';
import 'dayjs/locale/de';
import 'dayjs/locale/en';
import 'dayjs/locale/es';
import 'dayjs/locale/fr';
import 'dayjs/locale/id';
import 'dayjs/locale/it';
import 'dayjs/locale/ja';
import 'dayjs/locale/ko';
import 'dayjs/locale/pt';
import 'dayjs/locale/tr';
import 'dayjs/locale/vi';
import 'dayjs/locale/zh-cn';

import { DEFAULT_LOCALE } from '../../constants';

dayjs.extend(utc);
dayjs.extend(customParseFormat);
dayjs.extend(localeData);
dayjs.extend(advancedFormat);
dayjs.extend(localizedFormat);
dayjs.extend(preParsePostFormat);
dayjs.extend(weekOfYear);

type DateInput = dayjs.ConfigType;

const normalizeDayjsLocale = (locale: string) =>
  locale.toLowerCase().split('-')[0];

const MINUTES = 'minute';

const american = (date: DateInput) => dayjs(date).locale('en').format('L');

const defaultValueFormat = (date: DateInput) =>
  dayjs(date).format('YYYY-MM-DD');

const shortMonthDateFormat = (date: DateInput, locale = 'en') =>
  dayjs(date).locale(normalizeDayjsLocale(locale)).format('ll');

const shortDateFormat = (date: DateInput, locale = 'en') =>
  dayjs(date).locale(normalizeDayjsLocale(locale)).format('l');

const fromUnix = (date: number) =>
  dayjs.unix(date).locale(DEFAULT_LOCALE).format();

const now = () => dayjs().locale(DEFAULT_LOCALE).format();

const utcParse = (date: DateInput, format: string) =>
  dayjs.utc(date, format, true);

const utcFormat = (date: DateInput, format?: string) =>
  dayjs.utc(date).format(format);

const prettyLocale = (date: DateInput, locale = 'en') =>
  dayjs(date).locale(normalizeDayjsLocale(locale)).format('LL');

const future = (period: number, fraction: string, format?: string) =>
  dayjs()
    .add(period, fraction as dayjs.ManipulateType)
    .locale(DEFAULT_LOCALE)
    .format(format);

const isBefore = (date: DateInput, dateToCompare: DateInput) =>
  dayjs(date).isBefore(dateToCompare);

const addMonths = (date: DateInput, unit: number) =>
  dayjs(date).add(unit, 'M').toDate();

const getCardinalNumber = (day: number, locale = 'en') => {
  const ordinal = dayjs()
    .locale(normalizeDayjsLocale(locale))
    .localeData()
    .ordinal(day);
  return ordinal.replace(/[[\]]/g, '');
};

const formatToBrowserLocale = (date: string) => {
  const locale = navigator.language;

  return new Date(date).toLocaleDateString(locale, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
};

export const date = {
  addMonths,
  american,
  defaultValueFormat,
  formatToBrowserLocale,
  fromUnix,
  future,
  getCardinalNumber,
  isBefore,
  MINUTES,
  now,
  prettyLocale,
  shortDateFormat,
  shortMonthDateFormat,
  utc: utcParse,
  utcFormat,
};
